import { useEffect, useState } from "react"
import { useMaterialUIController } from "context"

// @mui material components
import Grid from "@mui/material/Grid"
import CircularProgress from "@mui/material/CircularProgress"
import Card from "@mui/material/Card"
import Divider from "@mui/material/Divider"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Collapse from "@mui/material/Collapse"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import Icon from "@mui/material/Icon"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDPagination from "components/MDPagination"
import MDTypography from "components/MDTypography"

// Material Dashboard 2 React examples
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// Reportes page components
import ReportesInformation from "layouts/asistencia/reportes/components/ReportesInformation"

// React Router
import { useNavigate } from "react-router-dom"

// Config and JWT
import config from "config"
import { jwtDecode } from "jwt-decode"
import { showToast } from "utils/swalToast"

// Services
import {
  fetchRegistrosAsistencia,
  exportRegistrosAsistencia,
  fetchDependencias,
  fetchTiposAsistente,
  fetchAsistentes,
} from "layouts/asistencia/components/add/service"

function Reportes() {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const navigate = useNavigate()

  // Data states
  const [registros, setRegistros] = useState([])
  const [dependencias, setDependencias] = useState([])
  const [tiposAsistente, setTiposAsistente] = useState([])
  const [asistentes, setAsistentes] = useState([])

  // Loading and pagination
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)

  // Filters
  const [selectedOrganizacion, setSelectedOrganizacion] = useState("")
  const [selectedTipoAsistente, setSelectedTipoAsistente] = useState("")
  const [selectedAsistente, setSelectedAsistente] = useState("")
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadDependencias()
    loadTiposAsistente()
  }, [])

  useEffect(() => {
    if (selectedOrganizacion) {
      loadAsistentes()
    } else {
      setAsistentes([])
      setSelectedAsistente("")
    }
  }, [selectedOrganizacion])

  const loadDependencias = async () => {
    try {
      const data = await fetchDependencias()
      setDependencias(data || [])
    } catch (error) {
      console.error("Error loading organizaciones:", error)
    }
  }

  const loadTiposAsistente = async () => {
    try {
      const data = await fetchTiposAsistente()
      setTiposAsistente(data || [])
    } catch (error) {
      console.error("Error loading tipos de asistente:", error)
    }
  }

  const loadAsistentes = async () => {
    try {
      const data = await fetchAsistentes({ dependenciaId: selectedOrganizacion, size: 1000 })
      setAsistentes(data.content || [])
    } catch (error) {
      console.error("Error loading asistentes:", error)
    }
  }

  const loadRegistros = async (page) => {
    setLoading(true)
    try {
      const params = {
        page,
        size: 10,
      }

      if (selectedOrganizacion) {
        params.dependenciaId = selectedOrganizacion
      }

      if (selectedTipoAsistente) {
        params.tipoAsistenteId = selectedTipoAsistente
      }

      if (selectedAsistente) {
        params.asistenteId = selectedAsistente
      }

      if (fechaInicio) {
        params.fechaInicio = fechaInicio
      }

      if (fechaFin) {
        params.fechaFin = fechaFin
      }

      const result = await fetchRegistrosAsistencia(params)

      if (result.content) {
        setRegistros(result.content)
        setTotalPages(result.totalPages || 0)
        setTotalElements(result.totalElements || 0)
      } else {
        setRegistros([])
        setTotalPages(0)
        setTotalElements(0)
      }
    } catch (error) {
      console.error("Error loading registros:", error)
      setRegistros([])
    } finally {
      setLoading(false)
    }
  }

  const gotoPage = (p) => {
    setPageIndex(p)
    loadRegistros(p)
  }

  const reloadRegistros = () => {
    loadRegistros(pageIndex)
  }

  const handleBuscar = () => {
    setPageIndex(0)
    loadRegistros(0)
  }

  const handleClearFilters = () => {
    setSelectedOrganizacion("")
    setSelectedTipoAsistente("")
    setSelectedAsistente("")
    setFechaInicio("")
    setFechaFin("")
    setPageIndex(0)
    setRegistros([])
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const params = {}

      if (selectedOrganizacion) {
        params.dependenciaId = selectedOrganizacion
      }

      if (selectedTipoAsistente) {
        params.tipoAsistenteId = selectedTipoAsistente
      }

      if (selectedAsistente) {
        params.asistenteId = selectedAsistente
      }

      if (fechaInicio) {
        params.fechaInicio = fechaInicio
      }

      if (fechaFin) {
        params.fechaFin = fechaFin
      }

      // Nombre legible para el encabezado del Excel
      const dependenciaSel = dependencias.find((d) => d.id === selectedOrganizacion)
      if (dependenciaSel) params.dependenciaNombre = dependenciaSel.nombre

      const blob = await exportRegistrosAsistencia(params)

      const link = document.createElement("a")
      const url = URL.createObjectURL(blob)
      link.setAttribute("href", url)
      link.setAttribute(
        "download",
        `reporte_asistencia_${new Date().toISOString().split("T")[0]}.xlsx`
      )
      link.style.visibility = "hidden"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      showToast({ icon: "success", title: "Éxito", text: "Reporte exportado correctamente" })
    } catch (error) {
      console.error("Error al exportar:", error)
      showToast({ icon: "error", title: "Error", text: error.message || "Error al exportar el reporte" })
    } finally {
      setExporting(false)
    }
  }

  const convertToCSV = (data) => {
    const headers = [
      "Nombre",
      "Documento",
      "Tipo",
      "Dependencia",
      "Fecha",
      "Hora Entrada",
      "Hora Salida",
      "Duración",
      "Estado",
      "Registro Manual",
      "Usuario Registro",
    ]

    const rows = data.map((row) => [
      row.asistenteNombre || "",
      row.asistenteDocumentNumber || "",
      row.tipoAsistenteNombre || "",
      row.dependenciaNombre || "",
      row.fecha || "",
      row.horaEntrada || "",
      row.horaSalida || "",
      row.duracion || "",
      row.estadoAsistencia || "",
      row.registroManual ? "Sí" : "No",
      row.usuarioRegistro || "",
    ])

    return [headers, ...rows].map((row) => row.join(",")).join("\n")
  }

  const PaginationItems = () => {
    const pages = []
    let startPage = Math.max(pageIndex - 1, 0)
    let endPage = Math.min(pageIndex + 2, totalPages)

    for (let i = startPage; i < endPage; i++) {
      pages.push(i)
    }

    if (startPage > 0) {
      pages.unshift("...")
      pages.unshift(0)
    }

    if (endPage < totalPages) {
      pages.push("...")
      pages.push(totalPages - 1)
    }

    return (
      <>
        {pages.map((i, idx) =>
          i === "..." ? (
            <span key={idx} style={{ margin: "0 8px" }}>
              ...
            </span>
          ) : (
            <MDPagination item key={i} onClick={() => gotoPage(i)} active={pageIndex === i}>
              <MDTypography
                variant="caption"
                color={pageIndex === i ? "white" : darkMode ? "white" : "secondary"}
                sx={{ fontSize: 11 }}
              >
                {i + 1}
              </MDTypography>
            </MDPagination>
          )
        )}
      </>
    )
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={8}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              {/* Filtros */}
              <Card>
                <MDBox px={2}>
                  <MDBox display="flex" alignItems="center" py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                      Filtros
                    </MDTypography>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      onClick={handleExpandClick}
                      sx={{ cursor: "pointer", ml: 1 }}
                    >
                      {expanded ? (
                        <ExpandLessIcon fontSize="medium" />
                      ) : (
                        <ExpandMoreIcon fontSize="medium" />
                      )}
                    </MDBox>
                  </MDBox>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} pb={2}>
                      {/* Dependencia */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Dependencia</InputLabel>
                          <Select
                            label="Dependencia"
                            variant="outlined"
                            fullWidth
                            value={selectedOrganizacion}
                            onChange={(e) => setSelectedOrganizacion(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Todas</MenuItem>
                            {dependencias.map((org) => (
                              <MenuItem key={org.id} value={org.id}>
                                {org.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Tipo de Asistente */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Tipo de Asistente</InputLabel>
                          <Select
                            label="Tipo de Asistente"
                            variant="outlined"
                            fullWidth
                            value={selectedTipoAsistente}
                            onChange={(e) => setSelectedTipoAsistente(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Todos</MenuItem>
                            {tiposAsistente.map((tipo) => (
                              <MenuItem key={tipo.id} value={tipo.id}>
                                {tipo.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Asistente */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth disabled={!selectedOrganizacion}>
                          <InputLabel>Asistente</InputLabel>
                          <Select
                            label="Asistente"
                            variant="outlined"
                            fullWidth
                            value={selectedAsistente}
                            onChange={(e) => setSelectedAsistente(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Todos</MenuItem>
                            {asistentes.map((asistente) => (
                              <MenuItem key={asistente.id} value={asistente.id}>
                                {asistente.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Fecha Inicio */}
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Fecha Inicio"
                          type="date"
                          variant="outlined"
                          fullWidth
                          value={fechaInicio}
                          onChange={(e) => setFechaInicio(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      {/* Fecha Fin */}
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Fecha Fin"
                          type="date"
                          variant="outlined"
                          fullWidth
                          value={fechaFin}
                          onChange={(e) => setFechaFin(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      {/* Botones */}
                      <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <MDBox>
                            <MDButton type="submit" variant="gradient" color="info" onClick={handleBuscar}>
                              Buscar
                            </MDButton>
                            &nbsp;
                            <MDButton type="button" variant="outlined" color="info" onClick={handleClearFilters}>
                              Limpiar
                            </MDButton>
                            &nbsp;
                            <MDButton
                              type="button"
                              variant="gradient"
                              color="success"
                              onClick={handleExport}
                              disabled={exporting || registros.length === 0}
                            >
                              {exporting ? "Exportando..." : "Exportar"}
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Collapse>
                </MDBox>
              </Card>

              <Divider />

              {/* Tabla de Registros */}
              <Card pt={5} mt={3}>
                {/* Paginación Superior */}
                <MDBox mt={2} pr={2}>
                  <MDPagination>
                    {totalElements > 10 && (
                      <MDTypography
                        variant="overline"
                        color="light"
                        fontWeight="light"
                        verticalAlign="middle"
                      >
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          10 * pageIndex + 1
                        )}{" "}
                        a{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          Math.min((pageIndex + 1) * 10, totalElements)
                        )}{" "}
                        de{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          totalElements
                        )}
                        &nbsp;&nbsp;
                      </MDTypography>
                    )}
                    {pageIndex > 0 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex - 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                    )}
                    <PaginationItems />
                    {pageIndex < totalPages - 1 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex + 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    )}
                  </MDPagination>
                </MDBox>

                {/* Contenido */}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : registros && registros.length > 0 ? (
                  <ReportesInformation registros={registros} />
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <MDTypography variant="h6" color="text">
                      No hay datos para mostrar
                    </MDTypography>
                  </MDBox>
                )}

                {/* Paginación Inferior */}
                <MDBox mb={2} pr={2}>
                  <MDPagination>
                    {totalElements > 10 && (
                      <MDTypography
                        variant="overline"
                        color="light"
                        fontWeight="light"
                        verticalAlign="middle"
                      >
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          10 * pageIndex + 1
                        )}{" "}
                        a{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          Math.min((pageIndex + 1) * 10, totalElements)
                        )}{" "}
                        de{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          totalElements
                        )}
                        &nbsp;&nbsp;
                      </MDTypography>
                    )}
                    {pageIndex > 0 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex - 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                    )}
                    <PaginationItems />
                    {pageIndex < totalPages - 1 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex + 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    )}
                  </MDPagination>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default Reportes
